import {
  WS_CMD_REFRESH_CREDENTIALS,
  WS_CMD_REALTIME_MISSING_FRAMES,
  WS_CREDENTIAL_REFRESH_INTERVAL_MS,
  WS_EVENT_REALTIME_FRAMES,
  WS_EVENT_REALTIME_FRAMES_MISSING,
  WS_EVENT_WEBSOCKET_CREDENTIALS,
} from "./constants"
import { UlidDedupe } from "./dedupe"
import {
  describeCloseReason,
  describeReadyState,
  formatWsMessageLogArgs,
  redactWsUrl,
  wsLog,
  wsWarn,
} from "./logger"
import { parseWebsocketCredentials } from "@/lib/identity-setup"
import type {
  ClientWebSocketOptions,
  RealtimeFramesResponse,
  WebsocketCredentials,
  WsConnectionState,
  WsEventHandler,
  WsMessage,
} from "./types"

const DEFAULT_RECONNECT_ATTEMPTS = 12
const DEFAULT_RECONNECT_DELAY_MS = 5000
const CREDENTIAL_REFRESH_TIMEOUT_MS = 15_000
const REALTIME_FRAMES_TIMEOUT_MS = 5_000

function defaultWsBaseUrl(): string {
  const configured = process.env.NEXT_PUBLIC_WS_URL?.trim()
  if (configured) return configured.replace(/\/$/, "")
  if (typeof window === "undefined") return ""
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
  return `${protocol}//${window.location.host}/ws`
}

function buildWsUrl(base: string, token: string): string {
  const sep = base.includes("?") ? "&" : "?"
  return `${base}${sep}token=${encodeURIComponent(token)}`
}


export class ClientWebSocket {
  private socket: WebSocket | null = null
  private socketEpoch = 0
  private activeToken: string | null = null
  private credentials: WebsocketCredentials | null = null
  private handlers = new Map<string, Set<WsEventHandler>>()
  private wildcardHandlers = new Set<WsEventHandler>()
  private dedupe = new UlidDedupe()
  private state: WsConnectionState = "idle"
  private reconnectAttempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private intentionalClose = false
  private credentialRefreshInFlight = false
  private pendingCredentialRefresh: {
    resolve: (value: WebsocketCredentials | null) => void
    timeout: ReturnType<typeof setTimeout>
  } | null = null
  private pendingRealtimeFrames = new Map<
    string,
    {
      resolve: (value: RealtimeFramesResponse) => void
      timeout: ReturnType<typeof setTimeout>
    }
  >()

  private readonly options: Required<
    Pick<ClientWebSocketOptions, "reconnectAttempts" | "reconnectDelayMs" | "logging">
  > &
    ClientWebSocketOptions

  constructor(options: ClientWebSocketOptions = {}) {
    this.options = {
      reconnectAttempts: options.reconnectAttempts ?? DEFAULT_RECONNECT_ATTEMPTS,
      reconnectDelayMs: options.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS,
      logging: options.logging ?? true,
      ...options,
    }
  }

  private get log(): boolean {
    return this.options.logging
  }

  get connectionState(): WsConnectionState {
    return this.state
  }

  get token(): string | null {
    return this.activeToken
  }

  setOnStateChange(onStateChange: (state: WsConnectionState) => void): void {
    this.options.onStateChange = onStateChange
    onStateChange(this.state)
  }

  on(event: string, handler: WsEventHandler): () => void {
    if (event === "*") {
      this.wildcardHandlers.add(handler)
      return () => this.wildcardHandlers.delete(handler)
    }
    let set = this.handlers.get(event)
    if (!set) {
      set = new Set()
      this.handlers.set(event, set)
    }
    set.add(handler)
    return () => set?.delete(handler)
  }

  connect(credentials: WebsocketCredentials): void {
    if (this.intentionalClose) {
      this.intentionalClose = false
    }

    const sameSession =
      this.activeToken === credentials.token &&
      (this.state === "connected" || this.state === "connecting")
    if (sameSession) {
      return
    }

    this.credentials = credentials
    this.activeToken = credentials.token
    this.reconnectAttempt = 0
    this.clearTimers()

    const base = this.options.url ?? defaultWsBaseUrl()
    wsLog(
      this.log,
      "connecting",
      base ? redactWsUrl(buildWsUrl(base, credentials.token)) : "(no url)"
    )
    this.openSocket(credentials.token)
    this.scheduleCredentialRefresh()
  }

  disconnect(): void {
    this.intentionalClose = true
    this.activeToken = null
    this.clearTimers()
    this.clearPendingCredentialRefresh()
    this.clearPendingRealtimeFrames()
    this.dedupe.clear()
    this.closeSocket()
    this.setState("disconnected")
  }

  private setState(next: WsConnectionState, context?: string): void {
    if (this.state === next) return
    const prev = this.state
    this.state = next
    if (context) {
      wsLog(this.log, "state", prev, "→", next, "—", context)
    } else {
      wsLog(this.log, "state", prev, "→", next)
    }
    this.options.onStateChange?.(next)
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  /** Drop the current socket without triggering reconnect (handlers are detached). */
  private closeSocket(): void {
    const ws = this.socket
    this.socket = null
    if (!ws) return
    ws.onopen = null
    ws.onmessage = null
    ws.onerror = null
    ws.onclose = null
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close()
    }
  }

  private scheduleCredentialRefresh(): void {
    this.refreshTimer = setTimeout(() => {
      void this.refreshCredentialsOverSocket()
    }, WS_CREDENTIAL_REFRESH_INTERVAL_MS)
  }

  private clearPendingCredentialRefresh(): void {
    if (this.pendingCredentialRefresh) {
      clearTimeout(this.pendingCredentialRefresh.timeout)
      this.pendingCredentialRefresh.resolve(null)
      this.pendingCredentialRefresh = null
    }
  }

  private clearPendingRealtimeFrames(): void {
    for (const [requestId, pending] of this.pendingRealtimeFrames) {
      clearTimeout(pending.timeout)
      pending.resolve({
        event: WS_EVENT_REALTIME_FRAMES_MISSING,
        request_id: requestId,
        window_id: "",
      })
    }
    this.pendingRealtimeFrames.clear()
  }

  private requestCredentialsOverSocket(): Promise<WebsocketCredentials | null> {
    const ws = this.socket
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return Promise.resolve(null)
    }
    this.clearPendingCredentialRefresh()
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        if (this.pendingCredentialRefresh) {
          this.pendingCredentialRefresh = null
        }
        wsWarn(this.log, "credential refresh timed out")
        resolve(null)
      }, CREDENTIAL_REFRESH_TIMEOUT_MS)
      this.pendingCredentialRefresh = { resolve, timeout }
      ws.send(JSON.stringify({ command: WS_CMD_REFRESH_CREDENTIALS }))
    })
  }

  private tryResolveCredentialRefresh(message: WsMessage): boolean {
    if (message.event !== WS_EVENT_WEBSOCKET_CREDENTIALS) {
      return false
    }
    const pending = this.pendingCredentialRefresh
    if (!pending) {
      return false
    }
    clearTimeout(pending.timeout)
    this.pendingCredentialRefresh = null
    const creds = parseWebsocketCredentials(message)
    pending.resolve(creds)
    return true
  }

  private tryResolveRealtimeFrames(message: WsMessage): boolean {
    if (
      message.event !== WS_EVENT_REALTIME_FRAMES &&
      message.event !== WS_EVENT_REALTIME_FRAMES_MISSING
    ) {
      return false
    }
    const requestId = String(message.request_id ?? "")
    const pending = this.pendingRealtimeFrames.get(requestId)
    if (!pending) {
      return false
    }
    clearTimeout(pending.timeout)
    this.pendingRealtimeFrames.delete(requestId)
    pending.resolve(message as RealtimeFramesResponse)
    return true
  }

  requestRealtimeFrames(input: {
    windowId: string
    afterSeq: number
    throughSeq: number
  }): Promise<RealtimeFramesResponse> {
    const ws = this.socket
    const requestId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return Promise.resolve({
        event: WS_EVENT_REALTIME_FRAMES_MISSING,
        request_id: requestId,
        window_id: input.windowId,
      })
    }
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.pendingRealtimeFrames.delete(requestId)
        resolve({
          event: WS_EVENT_REALTIME_FRAMES_MISSING,
          request_id: requestId,
          window_id: input.windowId,
        })
      }, REALTIME_FRAMES_TIMEOUT_MS)
      this.pendingRealtimeFrames.set(requestId, { resolve, timeout })
      ws.send(
        JSON.stringify({
          command: WS_CMD_REALTIME_MISSING_FRAMES,
          request_id: requestId,
          window_id: input.windowId,
          after_seq: input.afterSeq,
          through_seq: input.throughSeq,
        })
      )
    })
  }

  private async refreshCredentialsOverSocket(): Promise<void> {
    if (this.intentionalClose || this.credentialRefreshInFlight) {
      return
    }
    if (this.state !== "connected") {
      this.scheduleCredentialRefresh()
      return
    }
    this.credentialRefreshInFlight = true
    try {
      wsLog(this.log, "requesting credential refresh over websocket")
      const next = await this.requestCredentialsOverSocket()
      if (!next || this.intentionalClose) {
        wsWarn(this.log, "credential refresh failed")
        return
      }
      this.credentials = next
      this.activeToken = next.token
      wsLog(
        this.log,
        "credentials refreshed",
        "expires",
        next.expires_at
      )
    } finally {
      this.credentialRefreshInFlight = false
      if (!this.intentionalClose) {
        this.scheduleCredentialRefresh()
      }
    }
  }

  private openSocket(token: string): void {
    if (this.intentionalClose) return

    const base = this.options.url ?? defaultWsBaseUrl()
    if (!base) {
      wsWarn(this.log, "no websocket URL (missing host?)")
      return
    }

    this.closeSocket()
    this.socketEpoch += 1
    const epoch = this.socketEpoch

    const openContext =
      this.reconnectAttempt > 0
        ? `opening socket (reconnect ${this.reconnectAttempt}/${this.options.reconnectAttempts})`
        : "opening socket"
    this.setState("connecting", openContext)
    const ws = new WebSocket(buildWsUrl(base, token))
    this.socket = ws

    ws.onopen = () => {
      if (epoch !== this.socketEpoch || this.intentionalClose) return
      this.reconnectAttempt = 0
      this.setState("connected")
    }

    ws.onmessage = (ev) => {
      if (epoch !== this.socketEpoch) return
      let message: WsMessage
      try {
        message = JSON.parse(String(ev.data)) as WsMessage
      } catch {
        wsWarn(this.log, "non-JSON message", ev.data)
        return
      }
      if (!message?.event) return
      if (this.tryResolveCredentialRefresh(message)) {
        return
      }
      if (this.tryResolveRealtimeFrames(message)) {
        return
      }
      if (this.dedupe.isDuplicate(message.ulid)) return
      wsLog(this.log, ...formatWsMessageLogArgs(message))
      this.dispatch(message)
    }

    ws.onerror = () => {
      if (epoch !== this.socketEpoch) return
      wsWarn(
        this.log,
        "connection error",
        describeReadyState(ws.readyState),
        "(browser WebSocket API does not expose error details)"
      )
      if (this.state !== "connected") {
        this.setState(
          "failed",
          `WebSocket error while ${describeReadyState(ws.readyState)}`
        )
      }
    }

    ws.onclose = (ev) => {
      if (epoch !== this.socketEpoch) return
      if (this.socket === ws) {
        this.socket = null
      }

      const closeDetail = describeCloseReason(ev.code, ev.reason, ev.wasClean)

      if (this.intentionalClose) {
        wsLog(this.log, "closed", closeDetail, "(intentional disconnect)")
        this.setState("disconnected", closeDetail)
        return
      }

      wsWarn(this.log, "connection closed", closeDetail)
      this.setState("disconnected", closeDetail)
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || !this.credentials) return
    if (this.reconnectTimer !== null) return
    if (this.reconnectAttempt >= this.options.reconnectAttempts) {
      const msg = `reconnect attempts exhausted (${this.options.reconnectAttempts})`
      wsWarn(this.log, msg)
      this.setState("failed", msg)
      return
    }
    this.reconnectAttempt += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.reconnect()
    }, this.options.reconnectDelayMs)
  }

  private reconnect(): void {
    if (this.intentionalClose || !this.credentials) return
    this.openSocket(this.credentials.token)
  }

  private dispatch(message: WsMessage): void {
    const handlers = this.handlers.get(message.event)
    if (handlers) {
      for (const fn of handlers) {
        fn(message)
      }
    }
    for (const fn of this.wildcardHandlers) {
      fn(message)
    }
  }
}
